import { useParams, useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Save, FileDown, Printer, Copy, Menu, X, Upload, FileText, Image, File, Trash2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import DocumentEditorNew from '@/components/documents/DocumentEditorNew';
import DocumentCreationInterface from '@/components/documents/DocumentCreationInterface';
import DocumentTemplates from '@/components/documents/DocumentTemplates';
import MultipleFileUploader from '@/components/fileUploader/MultipleFileUploader';
import { PromptInputBox } from '@/components/ui/ai-prompt-box';
import { CasoChatMessage } from '@/components/casos/CasoChatMessage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useSharedCasoGroups } from '@/hooks/useSharedCasoGroups';
import { useSharedUsers } from '@/hooks/useShareCaso';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getQuickResponse } from '@/context/chat/optimizations/complexityDetector';
import { extractPDFText, extractTextFromTextFile } from '@/utils/fileProcessing/textExtractors';
import '@/styles/casoDetail.css';
import '@/styles/casoDocuments.css';
import { CasoDocumentsPanel } from '@/components/casos/CasoDocumentsPanel';
import { convertTemplateToHtml } from '@/utils/templateToHtml';
import PrintableDocument from '@/components/documents/PrintableDocument';
import { useReactToPrint } from 'react-to-print';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tokensUsed?: { input: number; output: number; total: number };
  sources?: Array<{ label: string; url: string }>;
}

export default function CasoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Estados locais
  const [activeDocumentId, setActiveDocumentId] = useState<string | undefined>(undefined);
  const [documentContent, setDocumentContent] = useState('');
  const [streamingAiContent, setStreamingAiContent] = useState(''); // 🎬 Estado para streaming token-by-token
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | undefined>(undefined);
  const [editingDocTitle, setEditingDocTitle] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  
  // Ref e estado para geração de PDF
  const printableDocRef = useRef<HTMLDivElement>(null);
  const [pdfSettings, setPdfSettings] = useState<any>(null);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  
  const documentId = useMemo(() => id || "", [id]);

  // ✅ NOVO: Função para carregar dados da KB para o RAIL
  const handleLoadKBData = useCallback(async () => {
    console.log('[KB → RAIL][DEBUG] handleLoadKBData CHAMADO!', { casoId: id, userId: user?.id });
    
    if (!id || !user) {
      console.error('[KB → RAIL][DEBUG] Erro: caso ou usuário não identificado', { id, hasUser: !!user });
      toast.error('Caso não identificado');
      return;
    }

    try {
      console.log('[KB → RAIL][DEBUG] Buscando dados da KB para caso:', id);
      toast.info('Buscando dados da Base de Conhecimento...');

      // Buscar arquivos da KB com texto extraído
      const { data: kbFiles, error: kbError } = await supabase
        .from('caso_kb_links')
        .select('*')
        .eq('caso_id', id)
        .eq('status', 'processed')
        .not('extracted_text', 'is', null)
        .order('created_at', { ascending: false });

      if (kbError) throw kbError;

      if (!kbFiles || kbFiles.length === 0) {
        toast.warning('Nenhum documento processado na Base de Conhecimento');
        return;
      }

      // Combinar texto extraído de todos os documentos
      const combinedText = kbFiles
        .map(file => file.extracted_text)
        .filter(Boolean)
        .join('\n\n');

      if (!combinedText || combinedText.trim().length < 10) {
        toast.warning('Documentos não contêm dados estruturados');
        return;
      }

      // Extrair dados sensíveis usando regex (CPF, RG, Nome, Endereço, Telefone)
      const extractedData: Record<string, string[]> = {
        cpf: [],
        rg: [],
        names: [],
        phones: [],
        addresses: []
      };

      // Regex patterns
      const cpfRegex = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
      const rgRegex = /\b\d{1,2}\.?\d{3}\.?\d{3}-?[0-9Xx]\b/g;
      const phoneRegex = /\(?\d{2}\)?\s?\d{4,5}-?\d{4}/g;
      const nameRegex = /\b[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+(?:\s+[A-ZÁÉÍÓÚÂÊÎÔÛÀÈÌÒÙÇÑ][a-záéíóúâêîôûàèìòùçñ]+){1,5}\b/g;
      
      // Extrair CPFs
      const cpfMatches = combinedText.match(cpfRegex) || [];
      extractedData.cpf = [...new Set(cpfMatches)].slice(0, 5);

      // Extrair RGs
      const rgMatches = combinedText.match(rgRegex) || [];
      extractedData.rg = [...new Set(rgMatches)].slice(0, 5);

      // Extrair Telefones
      const phoneMatches = combinedText.match(phoneRegex) || [];
      extractedData.phones = [...new Set(phoneMatches)].slice(0, 5);

      // Extrair Nomes
      const nameMatches = combinedText.match(nameRegex) || [];
      extractedData.names = [...new Set(nameMatches.filter(name => 
        name.length >= 5 && 
        name.length <= 60 && 
        name.split(' ').length >= 2
      ))].slice(0, 5);

      // Extrair Endereços (heurística)
      const lines = combinedText.split('\n');
      const addressCandidates = lines
        .filter(line => {
          const trimmed = line.trim();
          return trimmed.length > 10 && 
                 (trimmed.toLowerCase().includes('rua ') || 
                  trimmed.toLowerCase().includes('av ') ||
                  trimmed.toLowerCase().includes('avenida ') ||
                  trimmed.match(/\d+/));
        })
        .slice(0, 3);
      extractedData.addresses = addressCandidates;

      // Salvar no aiDataStore usando o documentId atual
      const { useAIDocDataStore } = await import('@/store/aiDataStore');
      const store = useAIDocDataStore.getState();
      
      // Criar structured data
      const structured = {
        identification: {
          fullName: extractedData.names[0] || '',
          cpf: extractedData.cpf[0] || '',
          rg: extractedData.rg[0] || '',
        },
        contacts: {
          phones: extractedData.phones,
        },
        addresses: extractedData.addresses,
        sourceFiles: kbFiles.map(f => f.title).join(', ')
      };

      await store.saveOCR(activeDocumentId || 'kb-data', {
        rawText: combinedText.slice(0, 10000),
        structured
      }, user.id);

      const totalData = 
        extractedData.cpf.length + 
        extractedData.rg.length + 
        extractedData.names.length + 
        extractedData.phones.length + 
        extractedData.addresses.length;

      toast.success(`✅ ${totalData} dados carregados da KB (${kbFiles.length} documentos)`);
      console.log('[KB → RAIL] Dados extraídos:', extractedData);

    } catch (error) {
      console.error('[KB → RAIL] Erro:', error);
      toast.error('Erro ao buscar dados da KB');
    }
  }, [id, user, activeDocumentId]);


  // Carregar dados do caso
  const { data: caso, isLoading } = useQuery({
    queryKey: ['caso', id],
    queryFn: async () => {
      if (!id) throw new Error('ID do caso não fornecido');
      const { data, error } = await supabase
        .from('casos')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  // Carregar documentos do caso
  const { data: casoDocuments = [], isLoading: isLoadingDocuments } = useQuery({
    queryKey: ['caso-documents', id],
    queryFn: async () => {
      if (!id || !user) return [];
      const { data, error } = await supabase
        .from('caso_documents')
        .select('*')
        .eq('caso_id', id)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!id && !!user
  });

  // Carregar informações de compartilhamento
  const { data: sharedGroups, isLoading: isLoadingGroups } = useSharedCasoGroups(id || '');
  const { data: sharedUsers, isLoading: isLoadingUsers } = useSharedUsers(id || '');

  // Carregar histórico do chat
  useEffect(() => {
    if (!id || !user) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("caso_chats")
          .select("id, role, content, created_at")
          .eq("caso_id", id)
          .order("created_at", { ascending: true });
        if (error) {
          console.warn("Não foi possível carregar histórico do chat:", error);
          return;
        }
        const formatted: ChatMessage[] = (data ?? []).map((row: any) => ({
          id: row.id,
          role: row.role as 'user' | 'assistant',
          content: row.content,
          timestamp: new Date(row.created_at),
        }));
        setChatMessages(formatted);
      } catch (e) {
        console.warn("Erro ao carregar histórico:", e);
      }
    })();
  }, [id, user]);

  // Carregar arquivos enviados
  useEffect(() => {
    if (!id || !user) return;
    
    const loadUploadedFiles = async () => {
      setLoadingFiles(true);
      try {
        const { data, error } = await supabase
          .from('caso_kb_links')
          .select('*')
          .eq('caso_id', id)
          .order('created_at', { ascending: false });
        
        if (!error && data) {
          setUploadedFiles(data);
        }
      } catch (e) {
        console.warn("Erro ao carregar arquivos:", e);
      } finally {
        setLoadingFiles(false);
      }
    };
    
    loadUploadedFiles();
  }, [id, user]);

  // Auto-scroll para última mensagem (aguarda renderização completa)
  useEffect(() => {
    if (chatMessagesRef.current && chatMessages.length > 0) {
      // Usar setTimeout para garantir que as mensagens do banco já foram renderizadas
      const scrollTimer = setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 100);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [chatMessages, isTyping]);

  // Carregar configurações de PDF do usuário
  useEffect(() => {
    const loadPdfSettings = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('pdf_settings')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          console.error('Erro ao carregar configurações de PDF:', error);
          return;
        }
        
        if (data) {
          setPdfSettings(data);
          console.log('✅ Configurações de PDF carregadas:', data);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de PDF:', error);
      }
    };
    
    loadPdfSettings();
  }, [user?.id]);

  // Hook para impressão formatada (usar o mesmo ref do PDF)
  const handlePrintFormatted = useReactToPrint({
    contentRef: printableDocRef,
    documentTitle: casoDocuments.find(d => d.id === activeDocumentId)?.title || 'Documento',
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .abnt-format { 
          font-family: "Times New Roman", serif; 
          font-size: 12pt; 
          line-height: 1.5; 
        }
        .abnt-title { 
          text-align: center; 
          font-weight: bold; 
          margin-bottom: 12pt; 
          text-transform: uppercase; 
        }
        .abnt-paragraph { 
          text-align: justify; 
          text-indent: 2cm; 
          margin: 0 0 12pt 0; 
        }
        .abnt-subtitle { 
          font-weight: bold; 
          margin: 12pt 0 6pt 0; 
        }
        .abnt-list { 
          margin: 6pt 0; 
          padding-left: 2cm; 
        }
      }
    `
  });

  // Handlers do Upload - UPLOAD REAL COM KB
  // Helper para converter File em base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (!user || !id) {
      toast.error("Faça login para enviar arquivos.");
      return;
    }

    toast.info(`Processando ${files.length} arquivo(s)...`);

    const uploadPromises = files.map(async (file) => {
      try {
        // 1. Upload para Storage
        const timestamp = Date.now();
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `${user.id}/${id}/${timestamp}_${sanitizedName}`;

        const { error: uploadError } = await supabase.storage
          .from('caso-documents')
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // 2. Registrar no caso_kb_links
        const fileKind = file.type === 'application/pdf' ? 'pdf' 
          : file.type.includes('image') ? 'img'
          : file.type.includes('word') ? 'doc'
          : 'txt';

        const { data: linkData, error: linkError } = await supabase
          .from('caso_kb_links')
          .insert({
            caso_id: id,
            user_id: user.id,
            kind: fileKind,
            title: file.name,
            source_url: storagePath,
            status: 'processing',
            file_size: file.size
          })
          .select()
          .single();

        if (linkError) throw linkError;

        // ✅ Atualização otimista IMEDIATA
        setUploadedFiles(prev => [linkData, ...prev]);
        console.log(`[KB UPLOAD] ✅ Arquivo criado: ${file.name} (${linkData.id})`);

        // 3. Extrair texto
        let extractedText = '';
        if (file.type === 'application/pdf') {
          extractedText = await extractPDFText(file);
        } else if (file.type === 'text/plain') {
          extractedText = await extractTextFromTextFile(file);
        } else if (file.type.startsWith('image/')) {
          // Extrair texto de imagem via Google Vision OCR
          try {
            const base64 = await fileToBase64(file);
            // Normalizar MIME type: image/jpg -> image/jpeg
            const normalizedMime = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
            
            const { data: visionData, error: visionError } = await supabase.functions.invoke('google-vision', {
              body: { 
                imageBase64: base64, 
                mime: normalizedMime, 
                filename: file.name 
              }
            });
            
            if (!visionError && visionData?.text) {
              extractedText = visionData.text;
              toast.info(`OCR extraído de ${file.name}`);
            } else {
              extractedText = `[Imagem: ${file.name}]`;
            }
          } catch (e) {
            console.warn('Erro ao extrair OCR:', e);
            extractedText = `[Imagem: ${file.name}]`;
          }
        } else {
          extractedText = `[${file.name}]`;
        }

        // 4. Salvar texto completo no link (fallback para RAG)
        const { error: updateTextError } = await supabase
          .from('caso_kb_links')
          .update({ 
            extracted_text: extractedText.slice(0, 100000) // Limitar a 100k chars
          })
          .eq('id', linkData.id);

        if (updateTextError) {
          console.warn('⚠️ Erro ao salvar extracted_text:', updateTextError);
        }

        // 5. Processar e armazenar chunks se houver texto suficiente
        if (extractedText.length > 100) {
          const chunks = chunkText(extractedText, 500);
          
          // Gerar embeddings
          const { data: embData, error: embError } = await supabase.functions.invoke('generate-embeddings', {
            body: { texts: chunks }
          });

          if (!embError && embData?.embeddings) {
            // Inserir chunks no banco
            const chunkRecords = chunks.map((chunk, idx) => ({
              caso_id: id,
              user_id: user.id,
              link_id: linkData.id,
              content: chunk,
              source_url: storagePath,
              source_label: `${file.name} (parte ${idx + 1})`,
              embedding: embData.embeddings[idx]
            }));

            await supabase.from('kb_chunks').insert(chunkRecords);
          }

          // Atualizar status
          await supabase
            .from('caso_kb_links')
            .update({ status: 'processed' })
            .eq('id', linkData.id);

          // ✅ Atualizar estado local IMEDIATAMENTE
          setUploadedFiles(prev => 
            prev.map(f => f.id === linkData.id ? { ...f, status: 'processed' } : f)
          );
          console.log(`[KB UPLOAD] ✅ Status atualizado: ${file.name} → processed`);
        } else {
          // Marcar como processado mesmo sem chunks
          await supabase
            .from('caso_kb_links')
            .update({ status: 'processed' })
            .eq('id', linkData.id);

          // ✅ Atualizar estado local IMEDIATAMENTE
          setUploadedFiles(prev => 
            prev.map(f => f.id === linkData.id ? { ...f, status: 'processed' } : f)
          );
          console.log(`[KB UPLOAD] ✅ Status atualizado: ${file.name} → processed (sem chunks)`);
        }

        return { success: true, file: file.name };
      } catch (error) {
        console.error(`Erro ao processar ${file.name}:`, error);
        return { success: false, file: file.name, error };
      }
    });

    const results = await Promise.allSettled(uploadPromises);
    
    const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    const failCount = results.length - successCount;

    if (successCount > 0) {
      toast.success(`${successCount} arquivo(s) processado(s) com sucesso!`);
      setSelectedFiles([]);
    }
    if (failCount > 0) {
      toast.error(`${failCount} arquivo(s) falharam.`);
    }
  }, [user, id]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    toast.info('Arquivo removido');
  }, []);

  // Handlers para Criação de Documentos (Fase 3)
  const handleCreateDocument = useCallback(async (input: string, files?: File[]) => {
    if (!user || !id) {
      toast.error("Faça login para criar documentos.");
      return;
    }

    // Detectar documento em branco
    const isBlankDocument = input === '__BLANK_DOCUMENT__';

    setIsCreatingDocument(true);
    toast.info(isBlankDocument ? "Criando documento em branco..." : "Criando documento...");

    try {
      // 1. Criar registro do documento no banco
      const { data: newDoc, error: createError } = await supabase
        .from('caso_documents')
        .insert({
          caso_id: id,
          user_id: user.id,
          title: isBlankDocument ? 'Documento sem título' : 'Gerando documento...',
          type: 'DOCUMENTO_CHAT',
          content: isBlankDocument 
            ? '' 
            : '<p class="abnt-paragraph">Gerando conteúdo...</p>'
        })
        .select('id, title')
        .single();

      if (createError) throw createError;

      // 2. ABRIR DOCUMENTO
      setActiveDocumentId(newDoc.id);
      
      // Se for documento em branco, pular skeleton e IA
      if (isBlankDocument) {
        setDocumentContent('');
        await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
        toast.success("Documento em branco criado!");
        setIsCreatingDocument(false);
        return;
      }

      // 2.1. SKELETON LOADER (somente para documentos com IA)
      setDocumentContent(`
        <div style="padding: 60px 20px; text-align: center;">
          <!-- TEXTO NO TOPO -->
          <div style="margin-bottom: 40px;">
            <p style="font-size: 20px; color: #1e40af; font-weight: 600; margin-bottom: 8px;">
              <span style="display: inline-block; animation: spin 2s linear infinite; font-size: 28px; margin-right: 8px;">⏳</span>
              <strong>Gerando documento...</strong>
            </p>
            <p style="font-size: 15px; color: #666; font-weight: 400;">
              Em poucos segundos, seu documento estará pronto
            </p>
          </div>
          
          <!-- SKELETON LOADER ABAIXO -->
          <div style="max-width: 600px; margin: 0 auto;">
            <div style="height: 40px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 8px; margin-bottom: 16px;"></div>
            <div style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; width: 85%; margin: 0 auto 12px;"></div>
            <div style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; width: 90%; margin: 0 auto 12px;"></div>
            <div style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; width: 70%; margin: 0 auto 12px;"></div>
            <div style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; width: 80%; margin: 0 auto 12px;"></div>
            <div style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; width: 75%; margin: 0 auto;"></div>
          </div>
        </div>
        <style>
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        </style>
      `);
      await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
      
      // 3. Gerar conteúdo com IA (assíncrono, não bloqueia UI)
      try {
        console.log('🚀 [FRONTEND] Chamando generate-document com:', {
          prompt: input.substring(0, 100),
          casoId: id,
          userId: user.id,
          documentId: newDoc.id,
          bodySize: JSON.stringify({ prompt: input, casoId: id, userId: user.id, documentId: newDoc.id }).length
        });

        // 🔥 Usando fetch() direto para garantir que o body chegue corretamente
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;

        if (!accessToken) {
          throw new Error('Usuário não autenticado');
        }

        const bodyPayload = {
          prompt: input,
          casoId: id,
          userId: user.id,
          documentId: newDoc.id
        };

        console.log('🚀 [FETCH] Enviando requisição direta com body:', {
          ...bodyPayload,
          bodyStringLength: JSON.stringify(bodyPayload).length
        });

        const response = await fetch(
          `https://jfdgdjxvzsmuwofclqyt.supabase.co/functions/v1/generate-document`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
              'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmZGdkanh2enNtdXdvZmNscXl0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMwNDAwMDksImV4cCI6MjA1ODYxNjAwOX0.LdmCn6tAQE0J22dspr08r2WEStJphGBE4sZmcx6Fd9A'
            },
            body: JSON.stringify(bodyPayload)
          }
        );

        console.log('📡 [FETCH] Status da resposta:', response.status);

        let aiResponse = null;
        let aiError = null;

        if (response.ok) {
          aiResponse = await response.json();
          console.log('✅ [FETCH] Resposta recebida com sucesso');
          
          // 🎬 STREAMING TOKEN-BY-TOKEN via aiContent prop
          const generatedContent = aiResponse?.content || aiResponse?.data?.content;
          
          if (generatedContent) {
            console.log('🎬 Iniciando streaming de conteúdo...');
            
            // Dividir em palavras para simular streaming
            const words = generatedContent.split(' ');
            let streamedContent = '';
            
            // Stream em lotes de 3 palavras a cada 50ms
            for (let i = 0; i < words.length; i += 3) {
              const batch = words.slice(i, i + 3).join(' ') + ' ';
              streamedContent += batch;
              
              // ✅ Atualizar via aiContent (não tem bloqueio de hasAppliedContent)
              setStreamingAiContent(streamedContent);
              
              // Delay mínimo para efeito visual
              await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Garantir conteúdo final completo
            setStreamingAiContent(generatedContent);
            setDocumentContent(generatedContent);
            console.log('✅ Streaming concluído');
            
            // Limpar streaming após 500ms
            setTimeout(() => setStreamingAiContent(''), 500);
          }
        } else {
          const errorText = await response.text();
          console.error('❌ [FETCH] Erro na resposta:', errorText);
          aiError = new Error(`HTTP ${response.status}: ${errorText}`);
        }

        console.log('🔍 [DEBUG] aiResponse completo:', JSON.stringify(aiResponse, null, 2));
        console.log('🔍 [DEBUG] aiError:', aiError);

        // ✅ TRATAMENTO ESPECÍFICO PARA TIMEOUT/NETWORK ERRORS
        if (aiError && (
          aiError.message?.includes('Failed to send') ||
          aiError.message?.includes('FunctionsFetchError') ||
          aiError.message?.includes('fetch')
        )) {
          console.error('❌ [TIMEOUT] Edge function não respondeu a tempo');
          
          // Verificar se documento foi gerado mesmo com timeout
          const { data: checkDoc } = await supabase
            .from('caso_documents')
            .select('content')
            .eq('id', newDoc.id)
            .single();
          
          if (checkDoc && checkDoc.content && !checkDoc.content.includes('Gerando')) {
            // Documento foi gerado com sucesso (apesar do timeout no frontend)
            setDocumentContent(checkDoc.content);
            toast.success(`Documento "${newDoc.title}" gerado com sucesso!`);
            return;
          }
          
          // Se realmente falhou, deletar documento
          await supabase.from('caso_documents').delete().eq('id', newDoc.id);
          setActiveDocumentId(undefined);
          await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
          
          toast.error('Tempo limite excedido ao gerar documento. Tente novamente.', {
            duration: 5000
          });
          return;
        }

        if (aiError) {
          // ✅ TRATAMENTO ESPECÍFICO PARA ERRO DE CRÉDITOS
          const isCreditsError = 
            aiResponse?.error === 'Credit limit exceeded' || 
            aiError.message?.includes('429') ||
            aiError.message?.includes('Credit limit');
          
          if (isCreditsError) {
            // Deletar documento que ficou travado
            await supabase
              .from('caso_documents')
              .delete()
              .eq('id', newDoc.id);
            
            toast.error('Limite de créditos atingido. Faça upgrade ou aguarde o próximo ciclo.', {
              duration: 5000,
              action: {
                label: 'Ver Planos',
                onClick: () => navigate('/planos')
              }
            });
            
            setActiveDocumentId(undefined);
            await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
            return;
          }
          
          console.warn('⚠️ Erro na IA, mantendo documento com conteúdo inicial:', aiError);
          toast.warning('Documento criado, mas a IA não pôde gerar o conteúdo automaticamente.');
          return;
        }

        // 4. Atualizar documento com conteúdo gerado
        const generatedContent = aiResponse?.content || 
                                aiResponse?.data?.content || 
                                '<p class="abnt-paragraph">Erro ao gerar conteúdo. Edite manualmente.</p>';
        
        console.log('📄 [CASO-DOC] Conteúdo recebido:', generatedContent?.substring(0, 100));

        // ✅ ATUALIZAR ESTADO LOCAL IMEDIATAMENTE
        setDocumentContent(generatedContent);
        console.log('✅ [CASO-DOC] Estado local atualizado');
        
        const { error: updateError } = await supabase
          .from('caso_documents')
          .update({ content: generatedContent })
          .eq('id', newDoc.id);

        if (updateError) {
          console.warn('⚠️ Erro ao atualizar conteúdo:', updateError);
        } else {
          // ✅ Extrair título do documento gerado (primeiro H1 ou H2)
          let documentTitle = 'Documento Jurídico';
          const titleMatch = generatedContent.match(/<h[12][^>]*>(.*?)<\/h[12]>/i);
          if (titleMatch) {
            // Remover tags HTML e pegar só o texto
            documentTitle = titleMatch[1].replace(/<[^>]+>/g, '').trim();
          }

          // ✅ Atualizar título do documento no banco
          const { error: titleUpdateError } = await supabase
            .from('caso_documents')
            .update({ title: documentTitle })
            .eq('id', newDoc.id);

          if (titleUpdateError) {
            console.warn('⚠️ Erro ao atualizar título:', titleUpdateError);
          } else {
            console.log('✅ Título extraído e atualizado:', documentTitle);
          }

          toast.success(`Documento "${documentTitle}" gerado com sucesso!`);
          
          // Invalidar query para atualizar a lista de documentos com o novo título
          await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
        }

      } catch (aiError: any) {
        console.warn('⚠️ Erro na geração de conteúdo:', aiError);
        toast.warning('Documento criado, mas você precisará editá-lo manualmente.');
      }
      
    } catch (error: any) {
      console.error('❌ Erro fatal ao criar documento:', error);
      toast.error(error?.message || 'Erro ao criar documento');
    } finally {
      setIsCreatingDocument(false);
    }
  }, [user, id]);

  const handleNewDocument = useCallback(() => {
    setActiveDocumentId(undefined);
    setDocumentContent('');
    toast.info('Pronto para criar novo documento');
  }, []);

  const handleOpenDocument = useCallback(async (docId: string) => {
    console.log('📂 handleOpenDocument chamado:', { docId, activeDocumentId });
    
    // 🔄 ACCORDION TOGGLE: Se clicar no documento ativo, fecha
    if (activeDocumentId === docId) {
      console.log('🔄 Fechando documento ativo (toggle)');
      setActiveDocumentId(undefined);
      setDocumentContent('');
      setIsLoadingDocument(false);
      toast.info('Documento fechado');
      return;
    }
    
    // ✅ TROCA DE DOCUMENTO
    console.log('✅ Abrindo novo documento');
    
    try {
      // 1️⃣ Limpar conteúdo anterior e marcar como carregando
      setIsLoadingDocument(true);
      setDocumentContent('');
      
      // 2️⃣ Definir ID do documento ativo
      setActiveDocumentId(docId);
      
      // 3️⃣ Buscar documento do banco
      const { data: doc, error } = await supabase
        .from('caso_documents')
        .select('*')
        .eq('id', docId)
        .single();
      
      console.log('📊 Resultado da query:', { doc, error });
      
      if (error) {
        console.error('❌ Erro na query:', error);
        throw error;
      }
      
      if (!doc) {
        console.warn('⚠️ Documento não encontrado:', docId);
        toast.error('Documento não encontrado');
        setActiveDocumentId(undefined);
        setDocumentContent('');
        setIsLoadingDocument(false);
        return;
      }
      
      // 4️⃣ Atualizar conteúdo E remover loading
      setDocumentContent(doc.content || '');
      setIsLoadingDocument(false);
      toast.success(`"${doc.title}" aberto`);
      
    } catch (error) {
      console.error('❌ Erro ao abrir documento:', error);
      toast.error('Erro ao abrir documento');
      setActiveDocumentId(undefined);
      setDocumentContent('');
      setIsLoadingDocument(false);
    }
  }, [activeDocumentId]);

  const handleCloseDocument = useCallback(() => {
    setActiveDocumentId(undefined);
    setDocumentContent('');
  }, []);

  const handleEditDocument = useCallback(async (docId: string) => {
    // Buscar documento atual
    const doc = casoDocuments.find(d => d.id === docId);
    if (!doc) return;
    
    setEditingDocId(docId);
    setEditingDocTitle(doc.title);
    setIsEditDialogOpen(true);
  }, [casoDocuments]);

  const handleSaveEditDocument = useCallback(async () => {
    if (!editingDocId || !editingDocTitle.trim()) {
      toast.error('Título não pode estar vazio');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('caso_documents')
        .update({ 
          title: editingDocTitle.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingDocId);
      
      if (error) throw error;
      
      toast.success('Documento atualizado com sucesso!');
      setIsEditDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
    } catch (error: any) {
      console.error('Erro ao editar documento:', error);
      toast.error('Erro ao editar documento');
    }
  }, [editingDocId, editingDocTitle, id, queryClient]);

  const handleDeleteDocument = useCallback(async (docId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('caso_documents')
        .delete()
        .eq('id', docId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Se o documento deletado estava aberto, fechar editor
      if (activeDocumentId === docId) {
        setActiveDocumentId(undefined);
        setDocumentContent('');
      }
      
      toast.success('Documento excluído com sucesso!');
      await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
    } catch (error: any) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento');
    }
  }, [user, activeDocumentId, id, queryClient]);

  const handleShowTemplates = useCallback(() => {
    setShowTemplates(true);
  }, []);

  const handleSelectTemplate = useCallback(async (template: any) => {
    if (!user || !id) {
      toast.error("Faça login para criar documentos.");
      return;
    }

    setShowTemplates(false);
    setIsCreatingDocument(true);
    toast.info(`Criando "${template.name}"...`);

    try {
      // 1️⃣ CONVERTER TEMPLATE PARA HTML COM PARÁGRAFOS CORRETOS
      const templateContent = template.content 
        ? convertTemplateToHtml(template.content)
        : '<p>Conteúdo do template não disponível</p>';
      
      // 2️⃣ Criar documento no banco COM O CONTEÚDO DO TEMPLATE
      const { data: newDoc, error: createError } = await supabase
        .from('caso_documents')
        .insert({
          caso_id: id,
          user_id: user.id,
          title: template.name,
          type: 'TEMPLATE',
          content: templateContent
        })
        .select('id, title')
        .single();

      if (createError) throw createError;

      // 3️⃣ ABRIR DOCUMENTO IMEDIATAMENTE com o conteúdo pronto
      setActiveDocumentId(newDoc.id);
      setDocumentContent(templateContent);
      await queryClient.invalidateQueries({ queryKey: ['caso-documents', id] });
      
      toast.success(`"${template.name}" aberto e pronto para edição!`);
      
    } catch (error: any) {
      console.error('❌ Erro ao criar documento do template:', error);
      toast.error(error?.message || 'Erro ao criar documento');
    } finally {
      setIsCreatingDocument(false);
    }
  }, [user, id, queryClient]);

  // Auto-save com debounce (Fase 4)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDocumentContentChange = useCallback((newContent: string) => {
    setDocumentContent(newContent);
    
    // Limpar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Auto-save após 2 segundos de inatividade
    if (activeDocumentId) {
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const { error } = await supabase
            .from('caso_documents')
            .update({ 
              content: newContent,
              updated_at: new Date().toISOString()
            })
            .eq('id', activeDocumentId);
          
          if (error) throw error;
          
          console.log('💾 Auto-save realizado');
        } catch (error) {
          console.error('Erro no auto-save:', error);
        }
      }, 2000);
    }
  }, [activeDocumentId]);

  // Cleanup do timeout ao desmontar
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleDeleteUploadedFile = useCallback(async (fileId: string, storagePath: string) => {
    if (!confirm('Deseja realmente excluir este arquivo da base de conhecimento?')) {
      return;
    }
    
    try {
      // 1. Deletar chunks associados
      await supabase
        .from('kb_chunks')
        .delete()
        .eq('link_id', fileId);
      
      // 2. Deletar registro do link
      const { error: linkError } = await supabase
        .from('caso_kb_links')
        .delete()
        .eq('id', fileId);
      
      if (linkError) throw linkError;
      
      // 3. Deletar arquivo do Storage
      const { error: storageError } = await supabase.storage
        .from('caso-documents')
        .remove([storagePath]);
      
      if (storageError) console.warn('Erro ao deletar do storage:', storageError);
      
      // 4. Atualizar lista local
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('Arquivo removido da base de conhecimento');
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      toast.error('Erro ao remover arquivo');
    }
  }, []);

  // Função auxiliar: chunking de texto
  function chunkText(text: string, maxTokens: number): string[] {
    const maxChars = maxTokens * 4; // ~4 chars = 1 token
    const chunks: string[] = [];
    
    for (let i = 0; i < text.length; i += maxChars) {
      chunks.push(text.slice(i, i + maxChars));
    }
    
    return chunks;
  }

  // Persistir mensagem no banco
  const persistMessage = useCallback(async (role: 'user' | 'assistant', content: string) => {
    if (!user || !id) return null;
    try {
      const { data, error } = await supabase
        .from("caso_chats")
        .insert({ 
          caso_id: id,
          user_id: user.id, 
          role, 
          content 
        })
        .select("id")
        .single();
      if (error) {
        console.error("Erro ao persistir mensagem:", error);
        return null;
      }
      return data?.id as string;
    } catch (e) {
      console.error("Erro ao persistir mensagem:", e);
      return null;
    }
  }, [user, id]);

  // Handlers do Chat
  const handleSendMessage = useCallback(async (message: string, files?: File[]) => {
    if (!message?.trim()) return;
    if (!user || !id) {
      toast.error("Faça login para enviar mensagens.");
      return;
    }

    // ✅ VALIDAR CRÉDITOS ANTES DE ENVIAR (Plano FREE)
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { data: creditUsage } = await supabase
        .from('user_credits')
        .select('total_credits, limit_credits')
        .eq('user_id', user.id)
        .eq('month_year', currentMonth)
        .maybeSingle();

      if (creditUsage && creditUsage.total_credits >= creditUsage.limit_credits) {
        toast.error('Limite de créditos atingido. Faça upgrade ou aguarde o próximo ciclo.', {
          duration: 5000,
          action: {
            label: 'Ver Planos',
            onClick: () => navigate('/planos')
          }
        });
        return;
      }
    } catch (error) {
      console.error('Erro ao validar créditos:', error);
    }

    const localUserMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, localUserMsg]);
    await persistMessage("user", message);

    const quickResponse = getQuickResponse(message);
    if (quickResponse) {
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: quickResponse,
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, assistantMsg]);
      await persistMessage("assistant", quickResponse);
      return;
    }

    setIsTyping(true);
    
    // Criar mensagem vazia do assistente (será preenchida progressivamente)
    const assistantMsgId = crypto.randomUUID();
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      sources: []
    };
    setChatMessages(prev => [...prev, assistantMsg]);

    try {
      const history = [...chatMessages.slice(-10), { role: "user", content: message }];

      // FASE 5: Integração com documento ativo
      const activeDoc = activeDocumentId 
        ? casoDocuments.find(d => d.id === activeDocumentId)
        : null;

      const payload = {
        message,
        documentId: id, // ID do caso (para buscar KB)
        activeDocumentId: activeDocumentId || null, // ID do documento ativo
        userId: user.id,
        documentContent: activeDoc?.content || documentContent || "", // Conteúdo do documento ativo
        conversationHistory: history,
        kb: { enabled: true, topK: 6 },
      };

      console.log('📤 Enviando mensagem com contexto do documento:', {
        casoId: id,
        activeDocumentId: activeDocumentId || 'nenhum',
        hasContent: !!payload.documentContent
      });

      // ============= SSE STREAMING PARSER =============
      const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const { data: { session } } = await supabase.auth.getSession();

      const res = await fetch(`${FUNCTIONS_URL}/document-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok || !res.body) throw new Error("Erro ao iniciar streaming");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullAnswer = "";
      let receivedSources: any[] = [];
      let receivedTokens: any = null;

      // Consumir stream token por token
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          const lines = part.split("\n");
          const eventLine = lines.find(l => l.startsWith("event:"));
          const dataLine = lines.find(l => l.startsWith("data:"));
          
          const evt = eventLine?.replace("event: ", "").trim() || "";
          const data = dataLine?.replace("data: ", "").trim() || "";

          if (evt === "token") {
            try {
              const parsed = JSON.parse(data);
              fullAnswer += parsed.token;
              
              // Atualizar última mensagem em tempo real
              setChatMessages(prev => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last.id === assistantMsgId) {
                  last.content = fullAnswer;
                }
                return copy;
              });
            } catch (e) {
              console.error("Erro ao parsear token:", e);
            }
          } else if (evt === "sources") {
            try {
              receivedSources = JSON.parse(data).sources || [];
            } catch (e) {
              console.error("Erro ao parsear sources:", e);
            }
          } else if (evt === "tokens") {
            try {
              receivedTokens = JSON.parse(data);
            } catch (e) {
              console.error("Erro ao parsear tokens:", e);
            }
          } else if (evt === "error") {
            try {
              const errorData = JSON.parse(data);
              throw new Error(errorData.message || "Erro no streaming");
            } catch (e) {
              console.error("Erro no streaming:", e);
            }
          }
        }
      }

      // Atualizar mensagem final com sources e tokens
      setChatMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last.id === assistantMsgId) {
          last.sources = receivedSources;
          if (receivedTokens) {
            last.tokensUsed = {
              input: receivedTokens.input || 0,
              output: receivedTokens.output || 0,
              total: receivedTokens.total || 0,
            };
          }
        }
        return copy;
      });

      // Persistir mensagem final
      await persistMessage("assistant", fullAnswer);

      // Feedback de créditos
      if (receivedTokens?.total) {
        toast.success(`Resposta gerada (${receivedTokens.total} créditos consumidos)`, {
          duration: 2000,
        });
      }

    } catch (e: any) {
      // Remover mensagem vazia em caso de erro
      setChatMessages(prev => prev.filter(m => m.id !== assistantMsgId));
      
      const msg = typeof e?.message === "string" ? e.message : "Falha ao consultar a IA.";
      if (msg.toLowerCase().includes("credit")) {
        toast.error("Créditos insuficientes.");
      } else {
        toast.error(msg);
      }
    } finally {
      setIsTyping(false);
    }
  }, [user, documentId, documentContent, chatMessages, persistMessage]);

  // Handlers do Editor
  const handleContentChange = useCallback((content: string) => {
    setDocumentContent(content);
  }, []);

  const handleSave = useCallback(async () => {
    if (!id) return;
    
    setIsSaving(true);
    try {
      // TODO: Salvar conteúdo no banco de dados
      // await supabase.from('caso_documentos').upsert({ caso_id: id, content: documentContent })
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Documento salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar documento');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [id, documentContent]);

  const handleDownloadPDF = useCallback(async () => {
    if (!printableDocRef.current) {
      toast.error('Erro: Conteúdo não disponível para gerar PDF');
      return;
    }
    
    if (!documentContent || documentContent.trim() === '') {
      toast.error('Documento vazio. Adicione conteúdo antes de gerar o PDF.');
      return;
    }
    
    setIsDownloadingPDF(true);
    
    try {
      // Nome do arquivo sanitizado
      const activeDoc = casoDocuments.find(d => d.id === activeDocumentId);
      const docTitle = activeDoc?.title || 'Documento';
      const sanitizedTitle = docTitle
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
      
      const filename = `${sanitizedTitle}_${Date.now()}.pdf`;
      
      // Importar dinamicamente a função de geração de PDF
      const { generatePdfFromElement } = await import('@/utils/pdfGenerator');
      
      // Configurações de geração
      const settings = pdfSettings || {};
      await generatePdfFromElement(printableDocRef.current, {
        filename,
        margin: settings.margin_left || 15,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          logging: false
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true
        }
      });
      
      toast.success('PDF gerado com sucesso!');
      console.log('✅ PDF baixado:', filename);
      
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsDownloadingPDF(false);
    }
  }, [documentContent, activeDocumentId, casoDocuments, pdfSettings]);

  const handlePrint = useCallback(() => {
    if (!printableDocRef.current) {
      toast.error('Erro: Conteúdo não disponível para impressão');
      return;
    }
    
    if (!documentContent || documentContent.trim() === '') {
      toast.error('Documento vazio. Adicione conteúdo antes de imprimir.');
      return;
    }
    
    console.log('🖨️ Iniciando impressão formatada...');
    handlePrintFormatted();
  }, [documentContent, handlePrintFormatted, activeDocumentId, casoDocuments]);

  const handleCopy = useCallback(() => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = documentContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    navigator.clipboard.writeText(textContent);
    toast.success('Conteúdo copiado!');
  }, [documentContent]);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => !prev);
  }, []);

  // Memoizar painel de documentos para evitar re-renders (ANTES de qualquer return condicional)
  const memoizedDocumentsPanel = useMemo(() => (
    <CasoDocumentsPanel
      documents={casoDocuments}
      activeDocumentId={activeDocumentId}
      onDocumentClick={handleOpenDocument}
      onNewDocument={handleNewDocument}
      onEditDocument={handleEditDocument}
      onDeleteDocument={handleDeleteDocument}
      isLoading={isLoadingDocuments}
      casoId={id}
      casoTitle={caso?.title || 'Caso sem título'}
    />
  ), [casoDocuments, activeDocumentId, handleOpenDocument, handleNewDocument, handleEditDocument, handleDeleteDocument, isLoadingDocuments, id, caso?.title]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Carregando caso...</div>
      </div>
    );
  }

  if (!caso) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="text-lg">Caso não encontrado</div>
        <Button onClick={() => navigate('/casos')}>
          Voltar para Casos
        </Button>
      </div>
    );
  }

  // Renderizar sidebar (desktop inline, mobile drawer)
  const renderSidebar = () => (
    <>
      {/* SEÇÃO UPLOAD */}
      <div className="caso-detail-upload-section" data-tour="caso-kb-upload">
        <h3 className="caso-kb-section-title">
          <Upload className="caso-kb-section-title-icon" />
          Base de Conhecimento
          <span className="caso-kb-section-title-count">
            ({uploadedFiles.length})
          </span>
        </h3>
        
        {/* Lista de arquivos enviados */}
        {loadingFiles ? (
          <div className="caso-kb-files-empty">Carregando arquivos...</div>
        ) : uploadedFiles.length > 0 ? (
          <div className="caso-kb-files-list">
            {uploadedFiles.map(file => (
              <div key={file.id} className="caso-kb-file-item">
                {file.kind === 'pdf' && <FileText className="caso-kb-file-icon" />}
                {file.kind === 'img' && <Image className="caso-kb-file-icon" />}
                {file.kind === 'doc' && <FileText className="caso-kb-file-icon" />}
                {file.kind === 'txt' && <File className="caso-kb-file-icon" />}
                
                <span className="caso-kb-file-name" title={file.title}>
                  {file.title}
                </span>
                
                <Badge 
                  variant={file.status === 'processed' ? 'default' : 'secondary'}
                  className="caso-kb-file-badge"
                >
                  {file.status === 'processed' ? 'OK' : file.status}
                </Badge>
                
                <button
                  className="caso-kb-file-delete"
                  onClick={() => handleDeleteUploadedFile(file.id, file.source_url)}
                  title="Excluir arquivo"
                  aria-label="Excluir arquivo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="caso-kb-files-empty">
            Nenhum arquivo enviado ainda
          </div>
        )}
        
        {/* Uploader (DropZone ou Botão Compacto) */}
        <MultipleFileUploader
          onFilesSelected={handleFilesSelected}
          selectedFiles={selectedFiles}
          onRemoveFile={handleRemoveFile}
          disabled={false}
          onClose={() => {}}
          hasUploadedFiles={uploadedFiles.length > 0}
        />
      </div>


      {/* SEÇÃO CHAT */}
      <div className="caso-detail-chat-section min-h-0 flex flex-col" data-tour="caso-chat">
        {chatMessages.length === 0 && !isTyping ? (
          <div className="caso-detail-chat-placeholder">CHAT</div>
        ) : (
          <div className="caso-detail-chat-messages min-h-0 flex-1 overflow-y-auto" ref={chatMessagesRef}>
            {chatMessages.map(msg => (
                  <CasoChatMessage
                    key={msg.id}
                    role={msg.role}
                    content={msg.content}
                    timestamp={msg.timestamp}
                    sources={msg.sources}
                  />
            ))}
            {isTyping && (
              <CasoChatMessage
                role="assistant"
                content="Digitando..."
              />
            )}
          </div>
        )}
        
        <div className="caso-detail-chat-input">
          <PromptInputBox
            onSend={handleSendMessage}
            placeholder="Faça perguntas..."
            disabled={isTyping}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="caso-detail-root">
      {/* HEADER FIXO */}
      <header className="caso-detail-header">
        <div className="caso-detail-header-left">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(caso.folder_id ? `/casos?folder=${caso.folder_id}` : '/casos')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="caso-detail-header-info" data-tour="caso-detail-header">
            <div className="flex items-center gap-2">
              <h1 className="caso-detail-title">
                Caso: {caso.title}
              </h1>
              {user && caso.user_id !== user.id && (
                <Badge variant="secondary" className="gap-1">
                  <Users className="w-3 h-3" />
                  Compartilhado
                </Badge>
              )}
              {user && caso.user_id === user.id && ((sharedGroups && sharedGroups.length > 0) || (sharedUsers && sharedUsers.length > 0)) && (
                <Badge variant="secondary" className="gap-1">
                  <Users className="w-3 h-3" />
                  {(sharedGroups?.length || 0) > 0 && `${sharedGroups.length} grupo${sharedGroups.length > 1 ? 's' : ''}`}
                  {(sharedGroups?.length || 0) > 0 && (sharedUsers?.length || 0) > 0 && ' · '}
                  {(sharedUsers?.length || 0) > 0 && `${sharedUsers.length} usuário${sharedUsers.length > 1 ? 's' : ''}`}
                </Badge>
              )}
            </div>
            <p className="caso-detail-description">
              {caso.description || 'Sem descrição'}
            </p>
          </div>
        </div>
        
        <div className="caso-detail-header-right">
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="gap-2"
            data-tour="caso-btn-save"
          >
            <Save className="h-4 w-4" />
            <span className="hidden sm:inline">Salvar</span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            className="gap-2"
            data-tour="caso-btn-pdf"
            disabled={isDownloadingPDF || !activeDocumentId || !documentContent}
          >
            <FileDown className={cn(
              "h-4 w-4",
              isDownloadingPDF && "animate-bounce"
            )} />
            <span className="hidden sm:inline">
              {isDownloadingPDF ? 'Gerando...' : 'PDF'}
            </span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="gap-2"
            data-tour="caso-btn-print"
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Imprimir</span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-2"
            data-tour="caso-btn-copy"
          >
            <Copy className="h-4 w-4" />
            <span className="hidden sm:inline">Copiar</span>
          </Button>
        </div>
      </header>

      {/* LAYOUT PRINCIPAL - 3 COLUNAS */}
      <div className="caso-detail-layout min-h-0">
        {/* COLUNA ESQUERDA - SIDEBAR (desktop inline) */}
        <aside className="caso-detail-sidebar min-h-0">
          {renderSidebar()}
        </aside>

        {/* COLUNA CENTRO - EDITOR ou INTERFACE DE CRIAÇÃO */}
        <main className="caso-detail-editor" data-tour="caso-document-creation">
          {showTemplates ? (
            // Visualização de modelos
            <DocumentTemplates
              onSelectTemplate={handleSelectTemplate}
              onBack={() => setShowTemplates(false)}
            />
          ) : !activeDocumentId ? (
            // Nenhum documento ativo: mostrar interface de criação
            <DocumentCreationInterface
              onCreateDocument={handleCreateDocument}
              onShowTemplates={handleShowTemplates}
              disabled={isCreatingDocument}
            />
          ) : (
            // Documento ativo: mostrar editor
            <>
              <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
                <h2 className="text-sm font-medium text-muted-foreground">
                  Editando documento
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseDocument}
                  className="h-8 text-xs"
                >
                  ✕ Fechar
                </Button>
              </div>
              {(() => {
                // Detectar se documento ativo é em branco
                const activeDoc = casoDocuments.find(d => d.id === activeDocumentId);
                const isActiveDocBlank = activeDoc 
                  ? (activeDoc.title === 'Documento sem título' && (!activeDoc.content || activeDoc.content.trim() === ''))
                  : false;
                
                return (
                  <DocumentEditorNew
                    document={{ 
                      id: activeDocumentId, 
                      title: activeDoc?.title || 'Documento',
                      caso_id: id 
                    }}
                    content={documentContent}
                    onContentChange={handleDocumentContentChange}
                    isBlankDocument={isActiveDocBlank}
                    isAIWriting={!!streamingAiContent}
                    aiContent={streamingAiContent}
                    isLoadingDocument={isLoadingDocument}
                    onSave={handleSave}
                    onDownloadPDF={handleDownloadPDF}
                    onPrint={handlePrint}
                    onCopy={handleCopy}
                    isDownloading={isSaving || isDownloadingPDF}
                    hideChatWidget={true}
                    hideHeader={true}
                    casoId={id}
                    onLoadKBData={handleLoadKBData}
                  />
                );
              })()}
            </>
          )}
        </main>

        {/* COLUNA DIREITA - PAINEL DE DOCUMENTOS */}
        {memoizedDocumentsPanel}
      </div>

      {/* MOBILE: Botão de toggle para abrir drawer */}
      <button
        onClick={toggleDrawer}
        className="caso-detail-mobile-toggle"
        aria-label="Toggle sidebar"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* MOBILE: Drawer overlay + sidebar */}
      {isDrawerOpen && (
        <>
          <div 
            className="caso-detail-drawer-overlay"
            onClick={toggleDrawer}
          />
          <div className="caso-detail-drawer">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Menu</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDrawer}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {renderSidebar()}
            </div>
          </div>
        </>
      )}

      {/* Dialog de edição de título */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="doc-title">Título do Documento</Label>
              <Input
                id="doc-title"
                value={editingDocTitle}
                onChange={(e) => setEditingDocTitle(e.target.value)}
                placeholder="Digite o novo título..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveEditDocument();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEditDocument}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documento invisível para geração de PDF */}
      {activeDocumentId && documentContent && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <PrintableDocument
            ref={printableDocRef}
            document={{
              id: activeDocumentId,
              title: casoDocuments.find(d => d.id === activeDocumentId)?.title || 'Documento',
              caso_id: id
            }}
            content={documentContent}
            pdfSettings={pdfSettings}
          />
        </div>
      )}
    </div>
  );
}
